import { environment } from '../../../environments/environment';

const BASE_URL = environment.serviceHostName;
const API_URL = `${BASE_URL}/v1`;
/** Show-admin backend — separate service in local dev, same in prod/staging. */
const ADMIN_API_URL = `${environment.adminServiceHostName}/api`;

//LOGIN
export const CHECK_EMAIL = `${API_URL}/auth`;
export const REQUEST_OTP = `${API_URL}/auth/request/otp`;
export const LOGIN = `${API_URL}/auth/login`;
export const LOGIN_OTP = `${API_URL}/auth/login/otp`;

export const EDUCATORS = `${API_URL}/educators`;
export const CREATE_PASSWORD = `${API_URL}/auth/password/create`;
export const LOGIN_OTP_REQUEST  = (show: string) =>`${API_URL}/${show}/Login/LoginotpRequest`;
export const VERIFY_OTP_REQUEST  = (show: string) => `${API_URL}/${show}/Login/VerifyOtpRequest`;
export const LOGOUT = (show: string) => `${API_URL}/${show}/Login/Logout`;
export const REFRESH_TOKEN = (show: string) => `${API_URL}/${show}/Login/RefreshToken`;

// SHOW ADMIN — divisions/classes for the home dashboard.
export const GET_DIVISIONS_CHECKIN_SUMMARY = (show: string) => `${API_URL}/${show}/Division/checkin-summary`;
/** The division/class catalogue. POSTs `{ entryUuid }` and is scoped to the show
 *  in the URL. Serves every show, unlike the retired `Master/GetShowsDivisions`. */
export const GET_SHOW_DIVISIONS = (show: string) => `${API_URL}/${show}/Division/GetShowDivisions`;
export const GET_COMBINATION_USAGE = (show: string) => `${API_URL}/${show}/Division/GetCombinationUsage`;

export const UPDATE_CLASS_CHECKIN_STATUS = (show: string) => `${API_URL}/${show}/Division/UpdateDivisionClassStatus`;
/** Configurations > Division Dates listing. POSTs `{ page, pageSize, sortBy,
 *  sortDirection, division }`. */
export const GET_DIVISION_DATES = (show: string) => `${API_URL}/${show}/Division/Divisions`;
/** Bulk update division dates/status/show type. POSTs a raw array of rows;
 *  `showType` must travel lower-cased. */
export const UPDATE_DIVISION_DATES = (show: string) => `${API_URL}/${show}/Division/UpdateDivisions`;

// SHOW ADMIN — clubs
/** Clubs with their exhibitor roster + count. POSTs `{ page, pageSize, sortBy,
 *  sortDirection, clubname }`; `Clubs` in the response is an escaped JSON array,
 *  each entry carrying a nested `FamilyMembers` array. */
export const GET_CLUB_EXHIBITORS = (show: string) => `${API_URL}/${show}/Club/GetClubExhibitors`;
/** Create/update/delete a club — POSTs `{ type: 'create'|'update'|'delete', ... }`
 *  in one body; answers `{ Success, ErrorCode?, Message?, Club }`. */
export const MANAGE_CLUB = (show: string) => `${API_URL}/${show}/Club/ManageClub`;
export const UPSERT_CLUB = (show: string) => `${API_URL}/${show}/Club/UpsertClub`;
export const UPDATE_CLUB_STATUS = (show: string) => `${API_URL}/${show}/Club/UpdateClubStatus`;
export const DELETE_CLUB = (show: string) => `${API_URL}/${show}/Club/DeleteClub`;
export const CHECK_CLUB_DELETE = (show: string) => `${API_URL}/${show}/Club/CheckClubDelete`;
/** Check if a club with the same name, county, and type already exists. */
export const CHECK_CLUB_DUPLICATE = (show: string) => `${API_URL}/${show}/Club/CheckClubDuplicate`;

// SHOW ADMIN — educators
/** One educator plus their club/show mappings (each with the club's address).
 *  Open to every role, so the educator portal reads its own profile with it. */
export const GET_EDUCATOR_DETAILS = (show: string) => `${API_URL}/${show}/Educator/GetEducatorDetails`;
export const VALIDATE_EDUCATOR_EMAIL_CHECK = (show: string) => `${API_URL}/${show}/Educator/ValidateEducatorEmailCheck`;

// SHOW ADMIN — entry approval. Still on the show-admin API, which reads the
// show from the `X-Abbreviation` header the auth interceptor adds.
export const UPDATE_ADMIN_ENTRY_APPROVAL_STATUS = `${ADMIN_API_URL}/entries/approvalstatus`;

// SHOW ADMIN — check-in. All endpoints are now show-scoped including confirmation email.
export const GET_ADMIN_CHECK_INS = (show: string) => `${API_URL}/${show}/CheckIn/GetCheckInsAsync`;
export const UPDATE_ADMIN_CHECK_IN = (show: string) => `${API_URL}/${show}/CheckIn`;
export const CREATE_ADMIN_CHECK_IN = (show: string) => `${API_URL}/${show}/CheckIn`;
export const SEND_CHECK_IN_CONFIRMATION = (show: string) => `${API_URL}/${show}/CheckIn/send-confirmation`;
export const GET_ADMIN_VALIDATION_TAG = `${ADMIN_API_URL}/validation-tags`;

// SHOW ADMIN — user accounts (family logins and educator logins).
/** Unified endpoint for family/educator users. POSTs `{ page, pageSize, type: 'family'|'educator', ... }`. */
export const GET_ALL_USER_DETAILS = (show: string) => `${API_URL}/${show}/User/GetAllUserDetails`;
/** Lock/unlock family user. POSTs `{ userId, isLocked }`. */
export const LOCK_FAMILY_USER = (show: string) => `${API_URL}/${show}/User/LockFamilyUser`;
/** Update educator club status (activate/deactivate). POSTs `{ educatorShowClubMappingUuid, isActive }`. */
export const UPDATE_EDUCATOR_CLUB_STATUS = (show: string) => `${API_URL}/${show}/Educator/UpdateEducatorClubStatus`;
/** Toggle whether an educator is the primary contact for a club. POSTs
 *  `{ educatorUuid, clubUuid, isPrimary }`. */
export const UPDATE_EDUCATOR_PRIMARY_FLAG = (show: string) => `${API_URL}/${show}/Educator/UpdateEducatorPrimaryFlag`;
/** Create/update an educator. `educatorUuid` present means update. `clubUuids`
 *  lists the clubs; `isPrimary` flags the educator as primary for them. */
export const UPSERT_EDUCATORS = (show: string) => `${API_URL}/${show}/Educator/UpsertEducators`;
/** Whether the signed-in state admin is mapped to `showUuid`. POSTs
 *  `{ email, roleUuid, showUuid }`; answers `data: true|false`. The show is read
 *  from the body, so the path takes the show-agnostic 'tsp'. Requires the bearer
 *  token, so it runs after login. */
export const GET_USER_INFO = (show: string = 'tsp') => `${API_URL}/${show}/User/GetUserInfo`;
export const VALIDATE_STATE_ADMIN_SHOW = (show: string = 'tsp') =>
  `${API_URL}/${show}/User/ValidationStateAdminShow`;

/* SHOW ADMIN — Configurations -------------------------------------------- */

export const GET_LOGIN_OPTIONS = (show: string) => `${API_URL}/${show}/Configuration/GetLoginOptions`;
export const UPDATE_LOGIN_OPTIONS = (show: string) => `${API_URL}/${show}/Configuration/UpdateLoginOptions`;

export const GET_CONFIGURATION_DATES = (show: string) => `${API_URL}/${show}/Configuration/GetConfigurationDates`;
export const UPDATE_CONFIGURATION_DATES = (show: string) => `${API_URL}/${show}/Configuration/UpdateConfigurationDates`;

export const GET_CONFIG_DIVISION_CLASSES = (show: string) => `${API_URL}/${show}/Configuration/GetConfigDivisionClasses`;
export const GET_SUB_DIVISION_FIELDS = (show: string) => `${API_URL}/${show}/Configuration/GetSubDivisionFields`;
export const UPDATE_SUB_DIVISION_FIELDS = (show: string) => `${API_URL}/${show}/Configuration/UpdateSubDivisionFields`;

export const GET_ALL_CLASSES_WITH_FEES = (show: string) => `${API_URL}/${show}/Configuration/GetAllClassesWithFees`;
export const UPDATE_CLASS_FEES = (show: string) => `${API_URL}/${show}/Configuration/UpdateClassFees`;

export const GET_ALL_PASSES_WITH_LATE_FEES = (show: string) => `${API_URL}/${show}/Configuration/GetAllPassesWithFees`;
export const UPDATE_PASS_FEES = (show: string) => `${API_URL}/${show}/Configuration/UpdatePassFees`;

export const GET_ADMIN_QUALITY_COUNT_BY_NUMBER = (show: string) => `${API_URL}/${show}/QualityCounts/GetQualityCountByQCNumber`;
export const UPLOAD_ADMIN_QUALITY_COUNTS = (show: string) => `${API_URL}/${show}/Export/ImportQualityCounts`;
export const GET_ADMIN_QUALITY_COUNTS_LAST_IMPORT = (show: string) => `${API_URL}/${show}/QualityCounts/GetLastImport`;
export const EXPORT_ADMIN_QUALITY_COUNTS =(show: string) => `${API_URL}/${show}/Export/ExportQualityCounts`;

export const GET_ADMIN_VALIDATION_TAGS = (show: string) => `${API_URL}/${show}/Configuration/GetValidationTags`;
export const GET_ADMIN_VALIDATION_TAG_BY_ID = (show: string) => `${API_URL}/${show}/Configuration/GetValidationTagById`;

// DYNAMIC FORMS — Field configuration API
/** Get features by role ID. Role-scoped, no show segment. Accepts raw JSON payload. */
export const GET_FEATURES_BY_ROLE = `${API_URL}/Role/GetFeaturesByRoleId`;
/** Get dynamic form field configuration by feature UUID. Accepts raw JSON payload. */
export const GET_DYNAMIC_FORM_BY_FEATURE = (show: string) => `${API_URL}/${show}/DynamicForm/GetDynamicformByFeautreUuid`;
/** Create a family from the register page. Anonymous — the family has no token
 *  yet — and the API resolves the family role itself. Takes `familyName`,
 *  `email` and `url`; sends the email-validation link. */
export const SAVE_FAMILY_DETAILS = (show: string) => `${API_URL}/${show}/FamilyRegistration/SaveFamilyDetails`;
/** Update family details with dynamic fields. Raw JSON of fieldKey: value pairs
 *  plus `familyUuid` and `url`; authenticated. */
export const UPDATE_FAMILY_DETAILS = (show: string) => `${API_URL}/${show}/FamilyRegistration/UpdateFamilyDetails`;

/** One endpoint for both add and edit; `id` of 0 (or absent) creates. */
export const UPSERT_ADMIN_VALIDATION_TAG = (show: string) => `${API_URL}/${show}/Configuration/UpsertValidationTag`;
export const UPLOAD_ADMIN_VALIDATION_TAGS = (show: string) => `${API_URL}/${show}/Export/ImportValidationTags`;
export const EXPORT_ADMIN_VALIDATION_TAGS = (show: string) => `${API_URL}/${show}/Export/ExportValidationTags`;

export const GET_ADMIN_PREMIUM_PASS_ALLOCATIONS = (show: string) => `${API_URL}/${show}/Configuration/GetPremiumPassAllocations`;
export const UPLOAD_ADMIN_PREMIUM_PASS_ALLOCATIONS = (show: string) => `${API_URL}/${show}/Export/ImportPremiumPassAllocations`;
/** Takes `{ allocationUuids }`; looks up each row's pass/club/educator, creates
 *  the cart entries, and flags the rows added-to-cart — all server-side.
 *  Answers `{ entries, notFound, alreadyAddedToCart }`. */
export const ADD_PREMIUM_PASS_ALLOCATIONS_TO_CART = (show: string) => `${API_URL}/${show}/Passes/AddPremiumPassToCart`;


// EDUCATOR
export const VALIDATE_EDUCATOR_SHOW_CLUB_MAPPING = (show: string) =>
  `${API_URL}/${show}/Educator/ValidateEducatorShowClubMapping`;

//FAMILY
export const VALIDATE_FAMILY_REGISTRATION = (show: string = 'tsp') => `${API_URL}/${show}/FamilyRegistration/Email-Validation`;
export const UPDATE_FAMILY_PROFILE_BY_SHOWADMIN = (show: string) => `${API_URL}/${show}/FamilyRegistration/UpdateFamilyProfileByStateadmin`;
export const GET_FAMILY_PROFILE = (show: string = 'tsp') => `${API_URL}/${show}/FamilyRegistration/GetFamilyProfile`;

//CLUBS
export const UPDATE_CLUB_AST_CEA_FLAG = (show: string) =>
  `${API_URL}/${show}/Club/UpdateAstCeaFlag`;

//QUALITY COUNTS
export const GET_QUALITY_COUNT_BY_QC_NUMBER = `${API_URL}/tsp/QualityCounts/GetQualityCountByQCNumber`;

//FAMILY MEMBERS
export const FAMILY_MEMBERS = `${API_URL}/family-members`;
export const SAVE_EXHIBITOR_BY_EDUCATOR = `${API_URL}/family-members/educator`;
export const GET_FORM_URL = `${API_URL}/forms`;

//ENTRIES / DIVISIONS / PASSES / LINE ITEMS
export const ENTRIES = `${API_URL}/entries`;
export const ENTRIES_BY_EXHIBITOR = `${ENTRIES}/byexhibitor`;
/** Entries grouped by club — powers the completed-entries PDF report. */
export const ENTRIES_BY_CLUB = `${ENTRIES}/byclub`;

/** Create or update an entry. The body is flat rather than an aggregate: the
 *  known keys become columns and the whole body is kept as `formData`.
 *  `entryUuid` present means update. */
export const UPSERT_ENTRY_FORM = (show: string) => `${API_URL}/${show}/Entries/UpsertEntryForm`;
/** Move a saved entry into the cart. POSTs `{ entryUuid }` — a body, not a query
 *  param — and answers with the entry it just moved. */
export const SUBMIT_ENTRY_FORM = (show: string) => `${API_URL}/${show}/Entries/SubmitEntryForm`;
/** Move an approved/submitted entry back to draft. POSTs `{ entryUuid }`; drops
 *  the entry's order, payment and check-ins along with the status. */
export const DRAFT_ENTRY_FORM = (show: string) => `${API_URL}/${show}/Entries/DraftEntry`;
/** Delete an entry. POSTs `{ entryUuid }`. */
export const DELETE_ENTRY_FORM = (show: string) => `${API_URL}/${show}/Entries/DeleteEntryForm`;
/** Send entries for review. POSTs `{ entryUuids }` and moves each to
 *  `submitted`. An entry already paid for keeps its order and payment status;
 *  an unpaid one has them rebuilt. */
export const SEND_ENTRY_APPROVAL = (show: string) => `${API_URL}/${show}/Entries/SendEntryApproval`;
/** The reviewer's decision on submitted entries. POSTs `{ entryUuids, status,
 *  comments }`; `status` is `approved` | `rejected` | `reportissue`, and the
 *  last two require `comments`. */
export const UPDATE_ENTRY_STATUS = (show: string) => `${API_URL}/${show}/Entries/UpdateEntryStatus`;
/**
 * The entry listing — and a single entry when `entryUuid` is in the body.
 *
 * Answers with a `PagedResult` (`{ items, totalCount, … }`). The only filters
 * it reads are `entryUuid`, `familyMemberUuid`, `familyUuid`, `clubUuid` and
 * `teamEntry`, plus paging — and it takes `familyUuid` from the body rather than
 * the JWT, so a family caller *must* send it or the page spans every family.
 */
export const GET_ALL_ENTRY_FORMS = (show: string) => `${API_URL}/${show}/Entries/GetAllEntryForms`;
export const TEAM_ENTRIES = `${API_URL}/team-entries`;
/** Show-scoped: the controller resolves the show from the route segment, and the
 *  rows arrive inside the `BaseResponse` envelope (`data`). Takes no query params —
 *  the API decides whether to include educator passes from the caller's JWT role. */
export const PASSES = (show: string) => `${API_URL}/${show}/Passes`;
/** Show-scoped passes API with filtering by type. POSTs filters in body including
 *  clubUuid and type (educator/exhibitor/premium). Migrated to GetShowPasses dynamic API. */
export const GET_PASSES = (show: string) => `${API_URL}/${show}/Passes/GetShowPasses`;
/** The caller's club roster with each educator's premium pass approvals. POSTs
 *  `{ page, pageSize }`; the club comes from the signed-in educator. Educators
 *  who have submitted nothing come back with a null `dateRequested`. */
export const GET_EDUCATOR_PREMIUM_PASS_APPROVALS = (show: string) =>
  `${API_URL}/${show}/Passes/GetEducatorPremiumPassApprovals`;
/** Create or update the caller's club premium pass request in a single call —
 *  `entryDetails` carries the desired quantities and replaces the prior request. */
export const UPSERT_PREMIUM_PASS_APPROVALS = (show: string) =>
  `${API_URL}/${show}/Passes/UpsertPremiumPassApprovals`;
/** Get premium pass approvals list from PremiumPassApproval table with resolved names. POSTs
 *  `{ page, pageSize, clubUuid?, status? }`. Returns club name, educator name, pass name. */
export const GET_PREMIUM_PASS_APPROVALS_LIST = (show: string) =>
  `${API_URL}/${show}/Passes/GetPremiumPassApprovalsList`;
/** Show-scoped, like the other new-API controllers: `/v1/{show}/LineItems`. */
export const LINE_ITEMS = (show: string) => `${API_URL}/${show}/LineItems`;
export const SAVE_LINE_ITEM_VALUES = (show: string) => `${LINE_ITEMS(show)}/SaveLineItemValues`;
export const UPDATE_LINE_ITEM_VALUES = (show: string) => `${LINE_ITEMS(show)}/UpdateLineItemValues`;
export const UPLOAD_LINE_ITEM_PHOTO = (show: string, entryUuid: string) =>
  `${LINE_ITEMS(show)}/${entryUuid}/image`;
export const LINE_ITEM_IMAGES = (show: string, entryUuid: string) =>
  `${LINE_ITEMS(show)}/${entryUuid}/images`;
export const DELETE_LINE_ITEM_PHOTO = (show: string, entryUuid: string, blobUuid: string) =>
  `${LINE_ITEMS(show)}/${entryUuid}/image/${blobUuid}`;

//NPS (Net Promoter Score)
export const CREATE_NPS = (show: string) => `${API_URL}/${show}/nps`;
export const GET_NPS_FORM_STATUS = (show: string) => `${API_URL}/${show}/nps`;

//ORDERS / PAYMENTS
export const ORDERS = (show: string) => `${API_URL}/${show}/orders`;
/**
 * The order listing, read straight off the tables rather than a stored
 * procedure — so it sees entries written through {@link UPSERT_ENTRY_FORM}.
 *
 * Answers with a `PagedResult` of **flat order-entry rows** (one per entry, each
 * carrying its `Entry`, `FamilyMember` and shared `PaymentInfo`), not the
 * pre-grouped orders the stored procedure returned. Rows sharing an
 * `orderNumber` are one submission, so callers group them.
 *
 * Filters it reads: `orderNumber`, `orderUuid`, `entryUuid`, `familyUuid`,
 * `educatorUuid`, `clubUuid`, `teamEntry`, `status[]`, `paymentStatus[]`,
 * `from`, `to`, plus `page` / `pageSize` / `sortBy` / `sortDirection`. Note
 * `page`, **not** `pageNumber`, and `familyUuid` comes from the body rather than
 * the JWT — omit it and the page spans every family.
 */
export const GET_ORDERS_DYNAMIC = (show: string) => `${ORDERS(show)}/GetOrdersDynamic`;
/** Records a received check against one or more awaiting orders. POSTs
 *  `{ orderNumbers, checkId, checkAmount }`. */
export const SUBMIT_CHECK_DETAILS = (show: string) => `${ORDERS(show)}/check-details`;
/** POSTs `{ submissionIds: number[], isCheckConfirmation: boolean }` to send confirmation email(s).
 *  `isCheckConfirmation` should be `true` for check payments, `false` for card payments. */
export const SEND_CONFIRMATION_EMAIL = (show: string) =>
  `${API_URL}/${show}/StripePayment/confirmation-email`;
export const REVISE_ORDER = (show: string) => `${ORDERS(show)}/revise`;
/** POSTs `multipart/form-data`: `ToEmails`, `CcEmails` (`;`-separated), `Subject`, `Body`, plus `Attachments` file parts. */
export const SEND_MASS_COMMUNICATION = (show: string) => `${API_URL}/${show}/Mail/mass-communication`;
/** Orders paid in one Stripe session — polled after the gateway hands back, until
 *  the webhook has confirmed them. POSTs `{ sessionId, retryAttempt }` in body. */
export const ORDERS_BY_SESSION = (show: string) =>
  `${API_URL}/${show}/StripePayment/session`;
/** Show-scoped, like the other `/v1/{show}/…` controllers: the API reads the show
 *  from the route segment and rejects the request outright without it. */
export const PAYMENT_CHECKOUT = (show: string) => `${API_URL}/${show}/StripePayment/checkout`;
export const PAY_BY_CHECK = (show: string) => `${API_URL}/${show}/StripePayment/check`;

//EXPORTS
export const UNIFIED_EXPORT = (show: string) => `${API_URL}/${show}/Export/UnifiedExport`;

//ORG CONFIGS
export const ORG_CONFIGS = `${API_URL}/org-configs`;
export const GET_ORG_CONFIGS = `${ORG_CONFIGS}/public`;

//FAMILY MEMBERS API
/** Same roster payload as GetFamilyMembers, scoped by `familyUuid` or `clubUuid`
 *  in the body — the educator portal filters by the club it has selected. */
export const GET_MEMBERS = (show: string = 'tsp') => `${API_URL}/${show}/FamilyMember/GetAllFamilyMembers`;
/** Family-scoped dashboard tile counts (members / shows / entries). */
export const GET_FAMILY_DASHBOARD = (show: string = 'tsp') => `${API_URL}/${show}/Exhibitor/GetFamilyDashboard`;
/** Show-scoped stats behind the educator home tiles. Same `{ FamilyPortal }`
 *  body as GetDashboardStats, but counts scoped to the show. */
export const GET_SHOW_STATS = (show: string) => `${API_URL}/${show}/Exhibitor/GetExhibitorStats`;
export const UPSERT_FAMILY_MEMBER = (show: string = 'tsp') => `${API_URL}/${show}/FamilyMember/UpsertFamilyMembers`;
export const DELETE_FAMILY_MEMBER = (show: string = 'tsp') => `${API_URL}/${show}/FamilyMember/DeleteFamilyMember`;
/** Un-registers an exhibitor from one show. Takes `{ familyMemberUuid, showCode }`;
 *  409s when the exhibitor still has entries there. */
export const DELETE_EXHIBITOR_MEMBER = (show: string = 'tsp') =>
  `${API_URL}/${show}/Exhibitor/DeleteExhibitorMember`;
/** Family behind a `familyEmail`, or 404 when none is registered under it. */
export const CHECK_FAMILY_EMAIL_EXIST = (show: string = 'tsp') =>
  `${API_URL}/${show}/Exhibitor/CheckFamilyEmailExist`;
/** Exhibitor roster, dynamic-form shaped: the rows carry their fields as a
 *  `formData` JSON string. Scope with `familyMemberUuid` / `familyUuid` /
 *  `clubUuid` / `showCode`;
 *  it pages and sorts but cannot search, so callers filter what it returns. */
export const GET_ALL_EXHIBITOR = (show: string = 'tsp') => `${API_URL}/${show}/Exhibitor/GetAllExhibitor`;

/** Registers one existing family member in the route's show. Raw JSON of
 *  fieldKey: value pairs plus `familyMemberUuid`; the whole body is stored as
 *  the member's `formData`, and the server stamps the family, user type and
 *  show itself. Needs a real show code in the route, not `tsp`. */
export const UPSERT_EXHIBITOR_MEMBER = (show: string) =>
  `${API_URL}/${show}/Exhibitor/UpsertExhibitorMember`;
export const FORM_STATUS_FOR_EXHIBITOR = (show: string) =>
  `${API_URL}/${show}/Exhibitor/FormStatusForExhibitor`;

//MASTER DATA
// Note: {show} placeholder needs to be replaced with actual show abbreviation
export const GET_ROLES = (show: string) => `${API_URL}/${show}/Master/GetRoles`;
export const GET_SHOWS = (show: string) => `${API_URL}/${show}/Master/GetShows`;
export const GET_ENVIRONMENT_CONFIGS = `${API_URL}/tsp/Master/GetEnvironmentConfigs`;
export const ENVIRONMENT_CONFIG_KEY_PARAM = 'configKey';
export const API_BASE_URL = API_URL;
export const SECURITY_ENC_DECRYPT = `${API_URL}/tsp/Security/EncDecrypt`;
export const ENVIRONMENT_CONFIG_KEYS = {
  ENCRYPTION_ENABLED: 'Encryption_Enabled',
  HANDLING_FEE: 'Payment_HandlingFee',
  CONVENIENCE_FEE_RATE: 'Payment_ConvenienceFeeRate'
} as const;
export const GET_CLUBS = (show: string = 'tsp') => `${API_URL}/${show}/Master/GetClubs`;
/** Competition types configured for a show (Open Show, Junior Show, …). */
export const GET_SHOW_TYPES = (show: string) => `${API_URL}/${show}/Master/GetShowTypes`;
/** Get list of all US states. */
export const GET_ALL_STATES = (show: string = 'tsp') => `${API_URL}/${show}/Master/GetAllStates`;
/** Get list of all counties for 4-H County dropdown. */
export const GET_COUNTIES = (show: string = 'tsp') => `${API_URL}/${show}/Master/GetCounties`;

// EVENT VALIDATION
/** Get event validation flags for UI control (Use Case 1) */
export const GET_EVENT_VALIDATION_FLAGS = (show: string) =>
  `${API_URL}/${show}/Master/GetEventValidationFlags`;
/** Validate event action before execution (Use Case 2) */
export const GET_EVENT_VALIDATION_MESSAGE = (show: string) =>
  `${API_URL}/${show}/Master/GetEventValidationMessage`;

// DIGITAL FORMS
export const CREATE_DIGITAL_FORM_SIGNING_REQUEST = (show: string) =>
  `${API_URL}/${show}/DigitalForms/create`;
